import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { formatLabel } from '@/lib/format-labels';

// ─── Branded assets ────────────────────────────────────────────────────────
import paynovusLogoHeader from '@/assets/paynovus-logo-horizontal.png';
import paynovusWatermark from '@/assets/paynovus-watermark-bg.png';
import paynovusLetterheadHeader from '@/assets/paynovus-letterhead-header.jpg';
import paynovusLetterheadFooter from '@/assets/paynovus-letterhead-footer.jpg';

// ─── Brand colors ──────────────────────────────────────────────────────────
const NAVY       = [15, 23, 42]    as const;
const NAVY_LIGHT = [30, 41, 59]    as const;
const NAVY_MID   = [51, 65, 85]    as const;
const WHITE      = [255, 255, 255] as const;
const GRAY_50    = [248, 250, 252]  as const;
const GRAY_100   = [241, 245, 249]  as const;
const GRAY_300   = [203, 213, 225]  as const;
const GRAY_500   = [100, 116, 139]  as const;
const GREEN      = [16, 185, 129]   as const;
const RED        = [239, 68, 68]    as const;
const AMBER      = [245, 158, 11]   as const;
const PRIMARY    = [59, 130, 246]   as const;
const SUNSET     = [249, 115, 22]   as const;

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yyyy'); } catch { return d; }
};
const fmtDateTime = (d: string | null | undefined) => {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yyyy HH:mm'); } catch { return d; }
};
const fmtMinor = (v: number | null | undefined, ccy?: string) => {
  if (v == null) return '—';
  const f = (v / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return ccy ? `${f} ${ccy}` : f;
};
const safe = (v: any, fallback = '—') => (v != null && v !== '' ? String(v) : fallback);

// Fetch an image from kyc-media storage and return as base64 data URL
async function fetchStorageImage(storageRef: string): Promise<string | null> {
  try {
    const path = storageRef.replace('storage:', '');
    const [bucket, ...rest] = path.split('/');
    const filePath = rest.join('/');
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(filePath, 300);
    if (error || !data?.signedUrl) return null;
    const resp = await fetch(data.signedUrl);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── Main Export ────────────────────────────────────────────────────────────
export async function exportClientProfilePDF(clientId: string): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  (doc as any).setCharSpace(0);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const mL = 14;
  const mR = 14;
  const cW = pageW - mL - mR;
  const ts = format(new Date(), 'yyyy-MM-dd HH:mm');
  let y = 0;
  let sectionNum = 0;
  const CONTENT_TOP = 28; // below letterhead header
  const CONTENT_BOTTOM = pageH - 24; // above letterhead footer

  // ══════════════════════════════════════════════════════════════════════════
  // FETCH ALL DATA IN PARALLEL
  // ══════════════════════════════════════════════════════════════════════════
  const [
    profileRes, accountsData, cardsRes, txnData, beneRes,
    alertsRes, casesRes, auditRes, riskRes, declarationsRes,
    screeningChecksRes, screeningHitsData, controlsData,
    whitelistRes, periodicReviewsRes, docRefreshRes,
    docsRes, aiRes, messagesData, onbDataRes,
    // NEW: EBA, card txns, sessions, corporate, baas, fee events
    ebaCompositeRes, ebaExtractRes, cardTxnsRes, loginAttemptsData,
    scaEventsRes, customerSessionsRes, corporateMembersData, baasContextData, feeEventsRes,
  ] = await Promise.all([
    supabase.from('client_profiles').select('*').eq('id', clientId).maybeSingle(),
    (async () => {
      const { data: accts } = await supabase.from('ledger_accounts').select('account_id, name, currency, status, account_type, iban, account_number, created_at').eq('client_id', clientId).order('created_at', { ascending: false });
      const ids = (accts || []).map((a: any) => a.account_id);
      if (ids.length === 0) return [];
      const [{ data: idents }, { data: bals }, { data: ctrls }] = await Promise.all([
        (supabase as any).from('account_identifiers').select('account_id, identifier_type, identifier_value, iban_mode, status, currency').in('account_id', ids).in('status', ['active','pending']),
        (supabase as any).from('account_balances').select('*').in('account_id', ids),
        supabase.from('account_controls').select('*').in('account_id', ids),
      ]);
      const iMap: Record<string, any[]> = {}, bMap: Record<string, any> = {}, ctrlMap: Record<string, any> = {};
      for (const i of (idents || [])) { (iMap[i.account_id] ||= []).push(i); }
      for (const b of (bals || [])) { bMap[b.account_id] = b; }
      for (const c of (ctrls || [])) { ctrlMap[c.account_id] = c; }
      return (accts || []).map((a: any) => ({ ...a, identifiers: iMap[a.account_id] || [], balance: bMap[a.account_id] || null, controls: ctrlMap[a.account_id] || null }));
    })(),
    supabase.from('cards').select('card_id, last4, status, product_type, plan_tier, created_at, expiry_month, expiry_year').eq('client_id', clientId).order('created_at', { ascending: false }),
    (async () => {
      const { data: accts } = await supabase.from('ledger_accounts').select('account_id').eq('client_id', clientId);
      const ids = (accts || []).map((a: any) => a.account_id);
      if (ids.length === 0) return [];
      const { data } = await (supabase as any).from('journal_lines')
        .select('journal_entry_id, account_id, currency, dr_cr, amount_minor, description, created_at, journal_entries(entry_type, status, description, created_at, tc_number)')
        .in('account_id', ids).order('created_at', { ascending: false }).limit(100);
      return data || [];
    })(),
    supabase.from('beneficiaries').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
    (supabase as any).from('tm_alerts').select('id, alert_type, severity, status, created_at, description, rule_id').eq('client_id', clientId).order('created_at', { ascending: false }).limit(50),
    (supabase as any).from('compliance_cases').select('id, case_number, case_type, status, priority, decision, created_at, closed_at, due_at, risk_score, risk_rating, assigned_to').eq('client_id', clientId).order('created_at', { ascending: false }).limit(50),
    supabase.from('admin_audit_log').select('id, action, actor_email, before_state, after_state, created_at, ip_address').eq('target_id', clientId).eq('target_type', 'client_profile').order('created_at', { ascending: false }).limit(50),
    (async () => {
      const { data: p } = await supabase.from('client_profiles').select('onboarding_session_id').eq('id', clientId).maybeSingle();
      if (!p?.onboarding_session_id) return null;
      const { data } = await (supabase as any).from('application_risk_scores').select('*').eq('session_id', p.onboarding_session_id).maybeSingle();
      return data;
    })(),
    (supabase as any).from('bnb_declarations').select('*').eq('client_id', clientId).order('created_at', { ascending: false }).limit(10),
    (supabase as any).from('screening_checks').select('*').eq('client_id', clientId).order('created_at', { ascending: false }).limit(20),
    (async () => {
      const { data: checks } = await (supabase as any).from('screening_checks').select('id').eq('client_id', clientId);
      if (!checks?.length) return [];
      const { data } = await (supabase as any).from('screening_hits').select('*').in('screening_check_id', checks.map((c: any) => c.id)).order('created_at', { ascending: false });
      return data || [];
    })(),
    (async () => {
      const { data: accts } = await supabase.from('ledger_accounts').select('account_id, name, currency').eq('client_id', clientId);
      const ids = (accts || []).map((a: any) => a.account_id);
      if (ids.length === 0) return [];
      const { data } = await supabase.from('account_controls').select('*').in('account_id', ids);
      return (data || []).map((c: any) => {
        const acc = (accts || []).find((a: any) => a.account_id === c.account_id);
        return { ...c, account_name: acc?.name, account_currency: acc?.currency };
      });
    })(),
    (supabase as any).from('client_whitelist_entries').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
    (supabase as any).from('periodic_reviews').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
    (supabase as any).from('document_refresh_tasks').select('*').eq('client_id', clientId).order('created_at', { ascending: false }),
    (async () => {
      const { data: p } = await supabase.from('client_profiles').select('onboarding_session_id').eq('id', clientId).maybeSingle();
      if (!p?.onboarding_session_id) return [];
      const { data } = await supabase.from('application_documents').select('*').eq('session_id', p.onboarding_session_id).order('created_at', { ascending: false });
      return data || [];
    })(),
    (async () => {
      const { data: p } = await supabase.from('client_profiles').select('onboarding_session_id').eq('id', clientId).maybeSingle();
      if (!p?.onboarding_session_id) return [];
      const { data } = await (supabase as any).from('ai_assessments').select('*').eq('session_id', p.onboarding_session_id).order('version', { ascending: false }).limit(3);
      return data || [];
    })(),
    (async () => {
      const { data: convos } = await (supabase as any).from('chat_conversations').select('id, participant_a, participant_b, last_message_at, created_at')
        .or(`participant_a.eq.${clientId},participant_b.eq.${clientId}`)
        .order('last_message_at', { ascending: false }).limit(5);
      if (!convos?.length) return { conversations: [], messages: [] };
      const { data: msgs } = await (supabase as any).from('chat_messages').select('id, conversation_id, sender_id, content, message_type, created_at, read_at')
        .in('conversation_id', convos.map((c: any) => c.id)).order('created_at', { ascending: false }).limit(50);
      return { conversations: convos, messages: msgs || [] };
    })(),
    (async () => {
      const { data: p } = await supabase.from('client_profiles').select('onboarding_session_id').eq('id', clientId).maybeSingle();
      if (!p?.onboarding_session_id) return null;
      const { data } = await (supabase as any).from('onboarding_sessions').select('id, onboarding_data, status, created_at, updated_at').eq('id', p.onboarding_session_id).maybeSingle();
      return data;
    })(),
    // ── NEW DATA FETCHES ──
    // EBA Composite Scores
    (supabase as any).from('compliance_composite_scores').select('*').eq('client_id', clientId).order('computed_at', { ascending: false }).limit(20),
    // EBA Extraction Results
    (supabase as any).from('compliance_extraction_results').select('id, extractor_code, score, risk_tier, triggered, raw_value, details, extracted_at, compliance_extractors(name, category, description)').eq('client_id', clientId).order('extracted_at', { ascending: false }).limit(100),
    // Card Transactions
    supabase.from('card_transactions').select('card_txn_id, amount_minor, currency, merchant_name, merchant_country, mcc, status, type, decline_reason, created_at, tc_number').eq('client_id', clientId).order('created_at', { ascending: false }).limit(100),
    // Login Attempts
    (async () => {
      const { data: p } = await supabase.from('client_profiles').select('email').eq('id', clientId).maybeSingle();
      if (!p?.email) return [];
      const { data } = await (supabase as any).from('login_attempts')
        .select('id, email, ip_address, user_agent, success, failure_reason, attempt_type, country, city, created_at')
        .eq('email', p.email).order('created_at', { ascending: false }).limit(100);
      return data || [];
    })(),
    // SCA Events
    (supabase as any).from('sca_events').select('id, operation, outcome, factor_type, ip_address, failure_reason, created_at').eq('client_id', clientId).order('created_at', { ascending: false }).limit(100),
    // Customer Sessions
    (supabase as any).from('customer_sessions').select('id, ip_address, device_type, country, city, login_at, last_activity_at, logout_reason, expired_at').eq('client_id', clientId).order('login_at', { ascending: false }).limit(50),
    // Corporate Members
    (async () => {
      const { data: p } = await supabase.from('client_profiles').select('client_type').eq('id', clientId).maybeSingle();
      if (p?.client_type !== 'corporate') return [];
      const { data } = await (supabase as any).from('corporate_members')
        .select('id, role, display_name, job_title, is_active, joined_at, kyc_status, kyc_verified_at')
        .eq('client_id', clientId).order('joined_at', { ascending: true });
      return data || [];
    })(),
    // BaaS Context
    (async () => {
      const { data: endCust } = await (supabase as any).from('baas_end_customers')
        .select('id, partner_id, partner_customer_ref, kyc_status, status, created_at')
        .eq('client_profile_id', clientId).limit(1).maybeSingle();
      if (!endCust) return null;
      const { data: partner } = await (supabase as any).from('baas_partners')
        .select('id, partner_name, legal_name, status').eq('id', endCust.partner_id).maybeSingle();
      return { endCustomer: endCust, partner };
    })(),
    // Fee Events (profitability)
    (async () => {
      const { data: accts } = await supabase.from('ledger_accounts').select('account_id').eq('client_id', clientId);
      const ids = (accts || []).map((a: any) => a.account_id);
      if (ids.length === 0) return [];
      const { data } = await (supabase as any).from('fee_events')
        .select('id, fee_type, amount_minor, currency, description, created_at')
        .in('account_id', ids).order('created_at', { ascending: false }).limit(50);
      return data || [];
    })(),
  ]);

  const profile = profileRes.data;
  if (!profile) throw new Error('Client profile not found');
  const accounts   = accountsData || [];
  const cards      = cardsRes.data || [];
  const txns       = txnData || [];
  const benes      = beneRes.data || [];
  const alerts     = alertsRes.data || [];
  const cases      = casesRes.data || [];
  const audit      = auditRes.data || [];
  const risk       = riskRes;
  const decls      = declarationsRes.data || [];
  const sChecks    = screeningChecksRes.data || [];
  const sHits      = screeningHitsData || [];
  const controls   = controlsData || [];
  const whitelist  = whitelistRes.data || [];
  const reviews    = periodicReviewsRes.data || [];
  const docRefresh = docRefreshRes.data || [];
  const docs       = docsRes || [];
  const aiAssess   = aiRes || [];
  const { conversations, messages } = messagesData;
  const onbSession = onbDataRes;
  // NEW data
  const ebaComposites   = ebaCompositeRes.data || [];
  const ebaExtracts     = ebaExtractRes.data || [];
  const cardTxns        = cardTxnsRes.data || [];
  const loginAttempts   = loginAttemptsData || [];
  const scaEvents       = scaEventsRes.data || [];
  const custSessions    = customerSessionsRes.data || [];
  const corpMembers     = corporateMembersData || [];
  const baasContext     = baasContextData;
  const feeEvents       = feeEventsRes || [];

  const isCorp = profile.client_type === 'corporate';
  const displayName = isCorp ? (profile.company_name || profile.full_name || 'Unknown') : (profile.full_name || 'Unknown');

  // ── Fetch document images from storage ────────────────────────────────────
  const docImages: Record<string, string | null> = {};
  const imageRefs: { key: string; ref: string }[] = [];
  for (const d of docs) {
    const ext = d.extracted_data as any;
    if (!ext) continue;
    // Main image
    if (ext.imageUrl) imageRefs.push({ key: `${d.document_type}_front`, ref: ext.imageUrl });
    if (ext.backImageUrl) imageRefs.push({ key: `${d.document_type}_back`, ref: ext.backImageUrl });
    if (ext.selfieUrl) imageRefs.push({ key: 'selfie_main', ref: ext.selfieUrl });
    // All images array
    if (ext.allImages) {
      for (const img of ext.allImages) {
        if (img.url && !imageRefs.some(r => r.ref === img.url)) {
          imageRefs.push({ key: `${d.document_type}_${img.type}`, ref: img.url });
        }
      }
    }
  }
  // Fetch up to 8 images
  const fetchResults = await Promise.all(imageRefs.slice(0, 8).map(async (r) => {
    const data = await fetchStorageImage(r.ref);
    return { key: r.key, data };
  }));
  for (const r of fetchResults) { docImages[r.key] = r.data; }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDERING HELPERS
  // ══════════════════════════════════════════════════════════════════════════
  const checkBreak = (needed: number) => {
    if (y + needed > CONTENT_BOTTOM) { doc.addPage(); y = CONTENT_TOP; }
  };

  const sectionHeader = (title: string) => {
    sectionNum++;
    checkBreak(16);
    y += 3;
    doc.setFillColor(...NAVY);
    doc.roundedRect(mL, y, cW, 9, 1.5, 1.5, 'F');
    // Sunset accent
    doc.setFillColor(...SUNSET);
    doc.roundedRect(mL, y, 3, 9, 1.5, 1.5, 'F');
    doc.setTextColor(...WHITE);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum}. ${title.toUpperCase()}`, mL + 6, y + 6);
    y += 13;
    doc.setTextColor(30, 30, 30);
  };

  const kvRow = (key: string, value: string) => {
    checkBreak(5.5);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY_MID);
    doc.text(key, mL + 3, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    const maxW = cW - 58;
    const lines = doc.splitTextToSize(safe(value), maxW);
    doc.text(lines, mL + 55, y);
    y += Math.max(lines.length * 4, 4.5);
  };

  const subHeader = (text: string) => {
    checkBreak(8);
    y += 2;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(text, mL + 2, y);
    doc.setDrawColor(...SUNSET);
    doc.setLineWidth(0.4);
    doc.line(mL + 2, y + 1.5, mL + 2 + doc.getTextWidth(text), y + 1.5);
    y += 6;
    doc.setTextColor(30, 30, 30);
  };

  const emptyNote = (text: string) => {
    doc.setFontSize(7.5); doc.setTextColor(...GRAY_500);
    doc.text(text, mL + 3, y); y += 5;
  };

  const renderTable = (head: string[][], body: string[][], opts?: any) => {
    autoTable(doc, {
      startY: y, head, body,
      styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak', textColor: [30,30,30] },
      headStyles: { fillColor: [...NAVY_LIGHT], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      alternateRowStyles: { fillColor: [...GRAY_50] },
      margin: { left: mL, right: mR },
      tableWidth: cW,
      ...opts,
    });
    y = (doc as any).lastAutoTable?.finalY + 4 || y + 8;
  };

  const drawBarChart = (items: { label: string; value: number; color: readonly [number,number,number] }[], title: string) => {
    checkBreak(items.length * 9 + 12);
    subHeader(title);
    const maxVal = Math.max(...items.map(i => i.value), 1);
    const barW = cW - 8;
    for (const item of items) {
      doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...GRAY_500);
      doc.text(`${item.label}: ${item.value}`, mL + 3, y); y += 2.5;
      doc.setFillColor(...GRAY_100);
      doc.roundedRect(mL + 3, y, barW, 5, 1, 1, 'F');
      const valW = Math.max((item.value / maxVal) * barW, 1);
      doc.setFillColor(...item.color);
      doc.roundedRect(mL + 3, y, valW, 5, 1, 1, 'F');
      y += 8;
    }
  };

  const addImage = (dataUrl: string, label: string, w = 45, h = 35) => {
    checkBreak(h + 8);
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY_MID);
    doc.text(label, mL + 3, y); y += 3;
    try {
      doc.addImage(dataUrl, 'JPEG', mL + 3, y, w, h);
      doc.setDrawColor(...GRAY_300); doc.rect(mL + 3, y, w, h, 'S');
    } catch { /* image failed */ }
    y += h + 4;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // COVER PAGE
  // ══════════════════════════════════════════════════════════════════════════
  // Watermark
  try {
    doc.saveGraphicsState();
    doc.setGState(new (doc as any).GState({ opacity: 0.04 }));
    doc.addImage(paynovusWatermark, 'PNG', pageW / 2 - 50, pageH / 2 - 60, 100, 120);
    doc.restoreGraphicsState();
  } catch { /* no watermark */ }

  // Letterhead header (logo banner)
  try {
    doc.addImage(paynovusLetterheadFooter, 'JPEG', 0, 0, pageW, 25);
  } catch {
    doc.setFillColor(...NAVY); doc.rect(0, 0, pageW, 25, 'F');
    try { doc.addImage(paynovusLogoHeader, 'PNG', 14, 6, 50, 16); } catch {
      doc.setTextColor(...WHITE); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
      doc.text('PAYNOVUS', 14, 18);
    }
  }

  // Cover content
  y = 35;
  doc.setFillColor(...NAVY); doc.roundedRect(mL, y, cW, 40, 3, 3, 'F');
  doc.setTextColor(...WHITE);
  doc.setFontSize(16); doc.setFont('helvetica', 'bold');
  doc.text('Client Profile Report', mL + 8, y + 12);
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(displayName, mL + 8, y + 20);
  doc.setFontSize(8);
  doc.setTextColor(200, 210, 230);
  doc.text(`System ID: ${profile.system_id || clientId.slice(0,8)}  |  ${formatLabel(profile.client_type)}`, mL + 8, y + 27);
  doc.text(`Generated: ${ts}  |  Classification: CONFIDENTIAL`, mL + 8, y + 33);

  // Status badges
  const statusCol = ['active','approved'].includes(profile.status) ? GREEN : profile.status === 'frozen' ? PRIMARY : AMBER;
  const riskCol = (profile.risk_level === 'low' || profile.risk_level === 'medium_low') ? GREEN : (profile.risk_level === 'medium' || profile.risk_level === 'medium_high') ? AMBER : RED;
  doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.setTextColor(...(statusCol as [number,number,number]));
  doc.text(`${formatLabel(profile.status).toUpperCase()}`, pageW - mR - 8, y + 14, { align: 'right' });
  doc.setTextColor(...(riskCol as [number,number,number]));
  doc.text(`Risk: ${formatLabel(profile.risk_level || 'unscored').toUpperCase()}`, pageW - mR - 8, y + 22, { align: 'right' });

  y += 48;

  // Executive summary
  doc.setFillColor(...GRAY_50); doc.roundedRect(mL, y, cW, 22, 2, 2, 'F');
  doc.setDrawColor(...GRAY_300); doc.roundedRect(mL, y, cW, 22, 2, 2, 'S');
  const sY = y + 5;
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY);
  doc.text('EXECUTIVE SUMMARY', mL + 4, sY);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(50,50,50);
  const col = (i: number) => mL + 4 + (i % 5) * (cW / 5);
  const row = (i: number) => sY + 5 + Math.floor(i / 5) * 6;
  const stats = [
    `Accounts: ${accounts.length}`, `Cards: ${cards.length}`, `Transactions: ${txns.length}`,
    `Beneficiaries: ${benes.length}`, `Alerts: ${alerts.length}`,
    `Cases: ${cases.length}`, `Screening: ${sChecks.length}`, `Hits: ${sHits.length}`,
    `Whitelists: ${whitelist.length}`, `Messages: ${messages.length}`,
  ];
  stats.forEach((s, i) => doc.text(s, col(i), row(i)));
  y += 28;

  // Letterhead footer (address)
  try { doc.addImage(paynovusLetterheadHeader, 'JPEG', 0, pageH - 22, pageW, 22); } catch {
    doc.setFontSize(7); doc.setTextColor(...GRAY_500);
    doc.text('Pozitano 2 sq., Perform Business Center, fl. 3, 1000 Sofia, Bulgaria', mL, pageH - 10);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 1. CLIENT IDENTITY
  // ══════════════════════════════════════════════════════════════════════════
  doc.addPage(); y = CONTENT_TOP;
  sectionHeader('Client Identity');
  const idFields = isCorp ? [
    ['System ID', profile.system_id], ['Tag', profile.hashtag],
    ['Company Name', profile.company_name], ['Registration Number', profile.company_registration_number],
    ['Company Country', profile.company_country], ['Contact Email', profile.email],
    ['Contact Phone', profile.phone_number], ['Client Type', formatLabel(profile.client_type)],
    ['Status', formatLabel(profile.status)], ['Risk Level', formatLabel(profile.risk_level || 'unscored')],
    ['KYC Status', formatLabel(profile.kyc_status || 'pending')],
    ['Source', formatLabel(profile.source || 'direct')],
    ['BaaS Partner ID', profile.baas_partner_id], ['BaaS Client Ref', profile.baas_client_ref],
    ['MCC Codes', profile.mcc_codes?.join(', ')],
    ['Show Mid Rate', profile.show_mid_rate ? 'Yes' : 'No'],
    ['Onboarded', fmtDate(profile.approved_at)], ['Approved By', profile.approved_by],
    ['Created', fmtDate(profile.created_at)], ['Last Updated', fmtDateTime(profile.updated_at)],
    ['User ID (Auth)', profile.user_id],
  ] : [
    ['System ID', profile.system_id], ['Tag', profile.hashtag],
    ['Full Name', profile.full_name], ['Email', profile.email],
    ['Phone', profile.phone_number], ['Date of Birth', profile.date_of_birth],
    ['Nationality', profile.nationality], ['Country of Residence', profile.country_of_residence],
    ['Client Type', formatLabel(profile.client_type)], ['Status', formatLabel(profile.status)],
    ['Risk Level', formatLabel(profile.risk_level || 'unscored')],
    ['KYC Status', formatLabel(profile.kyc_status || 'pending')],
    ['Source', formatLabel(profile.source || 'direct')],
    ['BaaS Partner ID', profile.baas_partner_id], ['BaaS Client Ref', profile.baas_client_ref],
    ['Show Mid Rate', profile.show_mid_rate ? 'Yes' : 'No'],
    ['Onboarded', fmtDate(profile.approved_at)], ['Approved By', profile.approved_by],
    ['Created', fmtDate(profile.created_at)], ['Last Updated', fmtDateTime(profile.updated_at)],
    ['User ID (Auth)', profile.user_id],
  ];
  for (const [k, v] of idFields) kvRow(k, safe(v));

  // ── Corporate Members (if corporate) ──
  if (corpMembers.length > 0) {
    subHeader(`Corporate Members (${corpMembers.length})`);
    renderTable(
      [['Name', 'Role', 'Job Title', 'Active', 'KYC Status', 'KYC Verified', 'Joined']],
      corpMembers.map((m: any) => [m.display_name || '—', formatLabel(m.role || '—'), m.job_title || '—', m.is_active ? 'Yes' : 'No', formatLabel(m.kyc_status || '—'), fmtDate(m.kyc_verified_at), fmtDate(m.joined_at)])
    );
  }

  // ── BaaS Context ──
  if (baasContext) {
    subHeader('BaaS End-Client Context');
    kvRow('Partner', baasContext.partner?.partner_name || baasContext.partner?.legal_name || '—');
    kvRow('Partner Status', formatLabel(baasContext.partner?.status || '—'));
    kvRow('Partner Customer Ref', baasContext.endCustomer?.partner_customer_ref || '—');
    kvRow('End-Client KYC', formatLabel(baasContext.endCustomer?.kyc_status || '—'));
    kvRow('End-Client Status', formatLabel(baasContext.endCustomer?.status || '—'));
    kvRow('Created', fmtDate(baasContext.endCustomer?.created_at));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 2. DOCUMENTS & VERIFICATION
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('Documents & Verification');

  if (docs.length > 0) {
    renderTable(
      [['Document Type', 'File Name', 'Verified', 'Verified By', 'Uploaded']],
      docs.map((d: any) => [
        formatLabel(d.document_type), d.file_name || '—',
        d.verified_at ? `Yes (${fmtDate(d.verified_at)})` : 'No',
        d.verified_by || '—', fmtDate(d.created_at),
      ])
    );

    // Extracted data per document
    for (const d of docs) {
      const ext = d.extracted_data as any;
      if (!ext || d.document_type === 'sumsub_raw_data') continue;

      subHeader(`${formatLabel(d.document_type)} — Extracted Data`);

      if (d.document_type === 'id_document') {
        const fields = [
          ['Full Name', ext.fullName], ['First Name', ext.firstName], ['Last Name', ext.lastName],
          ['Middle Name', ext.middleName],
          ['Date of Birth', ext.dateOfBirth], ['Nationality', ext.nationality],
          ['Document Type', ext.documentType], ['Document Number', ext.documentNumber],
          ['Document Country', ext.documentCountry],
          ['Issue Date', ext.issueDate], ['Expiry Date', ext.expiryDate],
          ['MRZ Line 1', ext.mrzLine1], ['MRZ Line 2', ext.mrzLine2], ['MRZ Line 3', ext.mrzLine3],
          ['Gender', ext.gender], ['NFC Verified', ext.withNfc ? 'Yes' : 'No'],
          ['SumSub Applicant ID', ext.sumsubApplicantId],
        ];
        for (const [k, v] of fields) { if (v) kvRow(k, safe(v)); }

        // Images
        const frontImg = docImages['id_document_front'] || docImages['id_document_id_front'];
        const backImg = docImages['id_document_back'] || docImages['id_document_id_back'];
        if (frontImg || backImg) {
          checkBreak(45);
          const startX = mL + 3;
          doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY_MID);
          doc.text('Identity Document Images', startX, y); y += 3;
          if (frontImg && backImg) {
            try { doc.addImage(frontImg, 'JPEG', startX, y, 42, 30); doc.setDrawColor(...GRAY_300); doc.rect(startX, y, 42, 30, 'S'); } catch {}
            try { doc.addImage(backImg, 'JPEG', startX + 48, y, 42, 30); doc.setDrawColor(...GRAY_300); doc.rect(startX + 48, y, 42, 30, 'S'); } catch {}
            doc.setFontSize(6); doc.setTextColor(...GRAY_500);
            doc.text('Front', startX + 18, y + 33); doc.text('Back', startX + 66, y + 33);
            y += 37;
          } else {
            const img = frontImg || backImg;
            if (img) addImage(img, frontImg ? 'ID Front' : 'ID Back');
          }
        }
      }

      if (d.document_type === 'selfie') {
        const fields = [
          ['Liveness Status', ext.liveness?.status], ['Liveness Confidence', ext.liveness?.confidence != null ? `${ext.liveness.confidence}%` : null],
          ['Face Match Status', ext.faceMatch?.status], ['Face Match Confidence', ext.faceMatch?.confidence != null ? `${ext.faceMatch.confidence}%` : null],
          ['Verification Status', ext.verificationStatus],
          ['SumSub Applicant ID', ext.sumsubApplicantId],
        ];
        for (const [k, v] of fields) { if (v) kvRow(k, safe(v)); }

        const selfieImg = docImages['selfie_main'] || docImages['selfie_selfie'];
        if (selfieImg) addImage(selfieImg, 'Selfie / Liveness Image');
      }

      if (d.document_type === 'proof_of_address') {
        const addr = ext.translatedAddress || ext.address;
        const fields = [
          ['Document Type', ext.documentType],
          ['Street', addr?.street], ['City', addr?.city || addr?.town],
          ['Postal Code', addr?.postalCode], ['Country', addr?.country],
          ['Address Source', ext.addressSource], ['Verification Method', ext.verificationMethod],
          ['SumSub Applicant ID', ext.sumsubApplicantId],
        ];
        // Also show original address if translated
        if (ext.translatedAddress && ext.address) {
          fields.push(['Original Street (Local)', ext.address.street]);
          fields.push(['Original City', ext.address.city || ext.address.town]);
        }
        for (const [k, v] of fields) { if (v) kvRow(k, safe(v)); }

        const poaImg = docImages['proof_of_address_front'] || docImages['proof_of_address_poa_front'];
        if (poaImg) addImage(poaImg, 'Proof of Address Document');
      }
    }

    // SumSub raw data details (AML screening, biometrics)
    const rawDoc = docs.find((d: any) => d.document_type === 'sumsub_raw_data');
    if (rawDoc?.extracted_data) {
      const raw = rawDoc.extracted_data as any;
      if (raw.biometrics) {
        subHeader('Biometric Verification (SumSub)');
        kvRow('Liveness Status', safe(raw.biometrics.liveness?.status));
        kvRow('Liveness Confidence', raw.biometrics.liveness?.confidence != null ? `${raw.biometrics.liveness.confidence}%` : '—');
        kvRow('Face Match Status', safe(raw.biometrics.faceMatch?.status));
        kvRow('Face Match Confidence', raw.biometrics.faceMatch?.confidence != null ? `${raw.biometrics.faceMatch.confidence}%` : '—');
      }
      if (raw.amlScreening) {
        subHeader('AML Screening Results (SumSub)');
        kvRow('Overall Risk', safe(raw.amlScreening.overallRisk));
        kvRow('PEP Match', raw.amlScreening.pepMatch ? 'YES' : 'No');
        kvRow('Sanctions Match', raw.amlScreening.sanctionsMatch ? 'YES' : 'No');
        kvRow('Adverse Media Match', raw.amlScreening.adverseMediaMatch ? 'YES' : 'No');
        kvRow('Adverse Media Score', safe(raw.amlScreening.adverseMediaScore));
        kvRow('Screening Date', fmtDateTime(raw.amlScreening.screeningDate));
      }
      if (raw.docStatus) {
        subHeader('Document Check Status (SumSub)');
        for (const [docType, status] of Object.entries(raw.docStatus) as any[]) {
          kvRow(`${docType}`, `${status.idDocType || docType} — ${status.reviewResult?.reviewAnswer || '—'}`);
        }
      }
      if (raw.applicantInfo?.info?.idDocs?.[0]?.address) {
        subHeader('SumSub Extracted Address');
        const addr = raw.applicantInfo.info.idDocs[0].address;
        const addrFields = [
          ['Street', addr.street], ['Street (EN)', addr.streetEn],
          ['Sub-street', addr.subStreet], ['Sub-street (EN)', addr.subStreetEn],
          ['Town', addr.town], ['Town (EN)', addr.townEn],
          ['State', addr.state], ['State (EN)', addr.stateEn],
          ['Country', addr.country], ['Formatted', addr.formattedAddress],
        ];
        for (const [k, v] of addrFields) { if (v) kvRow(k, safe(v)); }
      }
    }
  } else {
    emptyNote('No documents found in application records.');
  }

  if (docRefresh.length > 0) {
    subHeader('Document Refresh Tasks');
    renderTable(
      [['Document Type', 'Expiry', 'Due', 'Status', 'Requested']],
      docRefresh.map((d: any) => [formatLabel(d.document_type), fmtDate(d.document_expiry_date), fmtDate(d.due_at), formatLabel(d.status), fmtDate(d.request_sent_at)])
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 3. RISK & AI
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('Risk & AI Assessment');
  subHeader('Client Risk Profile');
  const riskFields = [
    ['Overall Risk Level', formatLabel(profile.risk_level || 'unscored')],
    ['Risk Score', safe(profile.risk_score)],
    ['PEP Status', profile.pep_status ? 'YES — Politically Exposed Person' : 'No'],
    ['Sanctions Status', profile.sanctions_status ? 'HIT' : 'Clear'],
    ['Adverse Media', profile.adverse_media_status ? 'HIT' : 'Clear'],
    ['EDD Status', formatLabel(profile.edd_status || 'not_required')],
    ['Monitoring Tier', profile.monitoring_tier ? `Tier ${profile.monitoring_tier}` : '—'],
    ['Last Screening Date', fmtDate(profile.last_screening_date)],
    ['ERM Risk Category', formatLabel(profile.erm_risk_category || '—')],
    ['ERM Inherent Score', safe(profile.erm_inherent_score)],
    ['ERM Residual Score', safe(profile.erm_residual_score)],
  ];
  for (const [k, v] of riskFields) kvRow(k, v);

  if (risk) {
    subHeader('Application Risk Score Breakdown');
    const sFields = [
      ['Total Score', safe(risk.total_score)], ['Risk Level', formatLabel(risk.risk_level || '—')],
      ['Auto Decision', formatLabel(risk.auto_decision || '—')], ['Decision Reason', safe(risk.decision_reason)],
      ['Country Score', safe(risk.country_score)], ['PEP Score', safe(risk.pep_score)],
      ['Income Score', safe(risk.income_score)], ['KYC Score', safe(risk.kyc_score)],
      ['EDD Required', risk.edd_required ? 'Yes' : 'No'],
      ['EDD Triggers', risk.edd_triggers?.join(', ') || '—'],
      ['Monitoring Tier', safe(risk.monitoring_tier)],
    ];
    for (const [k, v] of sFields) kvRow(k, v);
    drawBarChart([
      { label: 'Country', value: risk.country_score ?? 0, color: PRIMARY },
      { label: 'PEP', value: risk.pep_score ?? 0, color: AMBER },
      { label: 'Income', value: risk.income_score ?? 0, color: SUNSET },
      { label: 'KYC', value: risk.kyc_score ?? 0, color: GREEN },
    ], 'Risk Score Distribution');
  }

  if (aiAssess.length > 0) {
    subHeader('AI Assessment');
    for (const a of aiAssess.slice(0, 2)) {
      checkBreak(20);
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY_MID);
      doc.text(`Version ${a.version} — ${fmtDateTime(a.created_at)} — by ${a.generated_by || 'AI'}`, mL + 3, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(40,40,40);
      const lines = doc.splitTextToSize(a.assessment_text || '', cW - 6);
      for (const line of lines.slice(0, 25)) { checkBreak(4); doc.text(line, mL + 3, y); y += 3.5; }
      if (lines.length > 25) { doc.text(`… (${lines.length - 25} more lines)`, mL + 3, y); y += 4; }
      y += 3;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 3B. EBA RISK EXTRACTOR PROFILE
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('EBA Risk Extractor Profile');
  if (ebaComposites.length > 0 || ebaExtracts.length > 0) {
    const latestBatch = ebaComposites[0]?.batch_id;
    const latestComps = ebaComposites.filter((c: any) => c.batch_id === latestBatch || !latestBatch);
    const overall = latestComps.find((c: any) => c.category === 'overall') || latestComps[0];
    const overallScore = overall ? Number(overall.score) : 0;
    const overallTier = overall?.risk_tier || 'low';
    const triggeredCount = ebaExtracts.filter((r: any) => r.triggered).length;

    subHeader('Composite Risk Score');
    kvRow('Overall Score', `${overallScore.toFixed(1)} / 100`);
    kvRow('Risk Tier', formatLabel(overallTier));
    kvRow('Extractors Triggered', `${triggeredCount} of ${ebaExtracts.length}`);
    kvRow('Computed At', fmtDateTime(overall?.computed_at));

    // Category breakdown
    const CATEGORIES = ['client', 'company', 'transaction', 'access'];
    const catMap: Record<string, any> = {};
    for (const c of latestComps) catMap[c.category] = c;

    subHeader('Category Breakdown');
    const catItems: { label: string; value: number; color: readonly [number,number,number] }[] = [];
    for (const cat of CATEGORIES) {
      const comp = catMap[cat];
      const score = comp ? Number(comp.score) : 0;
      const tier = comp?.risk_tier || '—';
      kvRow(`${formatLabel(cat)} Score`, `${score.toFixed(0)} (${formatLabel(tier)})`);
      const col = score >= 75 ? RED : score >= 50 ? AMBER : score >= 25 ? SUNSET : GREEN;
      catItems.push({ label: formatLabel(cat), value: score, color: col });
    }
    drawBarChart(catItems, 'EBA Category Risk Distribution');

    // Risk score trend (if multiple overall scores)
    const trendComps = ebaComposites.filter((c: any) => c.category === 'overall');
    if (trendComps.length > 1) {
      subHeader('Risk Score Trend');
      renderTable(
        [['Date', 'Score', 'Tier']],
        trendComps.slice(0, 10).map((t: any) => [fmtDateTime(t.computed_at), Number(t.score).toFixed(1), formatLabel(t.risk_tier)])
      );
    }

    // Extractor results table
    if (ebaExtracts.length > 0) {
      subHeader(`Extractor Results (${ebaExtracts.length})`);
      renderTable(
        [['Code', 'Name', 'Category', 'Score', 'Tier', 'Triggered', 'Date']],
        ebaExtracts.slice(0, 40).map((r: any) => [
          r.extractor_code,
          r.compliance_extractors?.name || r.extractor_code,
          formatLabel(r.compliance_extractors?.category || '—'),
          Number(r.score).toFixed(0),
          formatLabel(r.risk_tier || '—'),
          r.triggered ? 'YES' : 'No',
          fmtDate(r.extracted_at),
        ]),
        { styles: { fontSize: 6.5, cellPadding: 1.5 } }
      );

      // Show triggered extractors with details
      const triggered = ebaExtracts.filter((r: any) => r.triggered && r.details);
      if (triggered.length > 0) {
        subHeader('Triggered Extractor Details');
        for (const r of triggered.slice(0, 15)) {
          checkBreak(14);
          doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...NAVY_MID);
          doc.text(`${r.compliance_extractors?.name || r.extractor_code} — Score: ${Number(r.score).toFixed(0)}`, mL + 3, y); y += 4;
          doc.setFont('helvetica', 'normal'); doc.setTextColor(50,50,50);
          const detailStr = Object.entries(r.details as Record<string, any>)
            .map(([k, v]) => `${formatLabel(k)}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join(' | ');
          const lines = doc.splitTextToSize(detailStr, cW - 8);
          for (const line of lines.slice(0, 4)) { checkBreak(4); doc.text(line, mL + 5, y); y += 3.5; }
          y += 2;
        }
      }
    }
  } else {
    emptyNote('No EBA risk extractor data available — scoring may not have been run yet.');
  }


  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader(`Accounts (${accounts.length})`);
  if (accounts.length > 0) {
    renderTable(
      [['Currency', 'Type', 'Name', 'IBAN / Identifier', 'Mode', 'Status', 'Opened']],
      accounts.map((a: any) => {
        const iban = a.identifiers?.[0]?.identifier_value || a.iban || '—';
        const mode = a.identifiers?.[0]?.iban_mode || '';
        return [a.currency, formatLabel(a.account_type), a.name || '—', iban, mode ? formatLabel(mode) : '—', formatLabel(a.status), fmtDate(a.created_at)];
      })
    );
    const accsWithBal = accounts.filter((a: any) => a.balance);
    if (accsWithBal.length > 0) {
      subHeader('Account Balances');
      renderTable(
        [['Account', 'CCY', 'Ledger Balance', 'Available', 'Pending DR', 'Pending CR', 'Last Txn']],
        accsWithBal.map((a: any) => [
          a.name || a.currency, a.balance.currency,
          fmtMinor(a.balance.ledger_balance_minor, a.balance.currency),
          fmtMinor(a.balance.available_balance_minor, a.balance.currency),
          fmtMinor(a.balance.pending_debit_minor), fmtMinor(a.balance.pending_credit_minor),
          fmtDate(a.balance.last_transaction_at),
        ])
      );
      drawBarChart(
        accsWithBal.map((a: any) => ({ label: `${a.name || a.currency}`, value: Math.abs(a.balance.available_balance_minor ?? 0) / 100, color: PRIMARY })),
        'Available Balances'
      );
    }
    // All identifiers
    const allIdents = accounts.flatMap((a: any) => (a.identifiers || []).map((i: any) => ({ ...i, accountName: a.name })));
    if (allIdents.length > 0) {
      subHeader('Account Identifiers');
      renderTable(
        [['Account', 'Type', 'Value', 'Mode', 'Status', 'Currency']],
        allIdents.map((i: any) => [i.accountName || '—', formatLabel(i.identifier_type), i.identifier_value, formatLabel(i.iban_mode || '—'), formatLabel(i.status), i.currency || '—']),
        { styles: { fontSize: 6.5, cellPadding: 1.5 } }
      );
    }
  } else { emptyNote('No accounts found.'); }

  // ── Profitability / Fee Events ──
  if (feeEvents.length > 0) {
    subHeader(`Fee Events / Revenue (${feeEvents.length})`);
    renderTable(
      [['Fee Type', 'Amount', 'CCY', 'Description', 'Date']],
      feeEvents.slice(0, 30).map((f: any) => [formatLabel(f.fee_type || '—'), fmtMinor(f.amount_minor), f.currency || '—', (f.description || '—').slice(0,50), fmtDate(f.created_at)]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
    const feeByType: Record<string, number> = {};
    for (const f of feeEvents) { feeByType[f.fee_type || 'other'] = (feeByType[f.fee_type || 'other'] || 0) + (f.amount_minor || 0); }
    const feeColors = [[59,130,246], [16,185,129], [245,158,11], [249,115,22], [239,68,68]] as [number,number,number][];
    drawBarChart(
      Object.entries(feeByType).map(([t, v], i) => ({ label: formatLabel(t), value: Math.abs(v) / 100, color: feeColors[i % feeColors.length] })),
      'Revenue by Fee Type'
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader(`Beneficiaries (${benes.length})`);
  if (benes.length > 0) {
    renderTable(
      [['Name', 'Bank / IBAN', 'Currency', 'Country', 'Status', 'Added']],
      benes.map((b: any) => [b.name || b.account_holder_name || '—', b.iban || b.account_number || '—', b.currency || '—', b.country || b.bank_country || '—', formatLabel(b.status || 'active'), fmtDate(b.created_at)])
    );
  } else { emptyNote('No beneficiaries registered.'); }

  // ══════════════════════════════════════════════════════════════════════════
  // 6. CARDS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader(`Cards (${cards.length})`);
  if (cards.length > 0) {
    renderTable(
      [['Card', 'Product', 'Tier', 'Status', 'Expiry', 'Issued']],
      cards.map((c: any) => [`•••• ${c.last4 || '????'}`, formatLabel(c.product_type), formatLabel(c.plan_tier), formatLabel(c.status), c.expiry_month && c.expiry_year ? `${String(c.expiry_month).padStart(2,'0')}/${c.expiry_year}` : '—', fmtDate(c.created_at)])
    );
  } else { emptyNote('No cards issued.'); }

  // ── Card Transactions (from card_transactions table) ──
  if (cardTxns.length > 0) {
    subHeader(`Card Transactions (${cardTxns.length})`);
    renderTable(
      [['Ref', 'Merchant', 'Country', 'MCC', 'Amount', 'CCY', 'Type', 'Status', 'Decline', 'Date']],
      cardTxns.slice(0, 60).map((t: any) => [t.tc_number || '—', (t.merchant_name || '—').slice(0,25), t.merchant_country || '—', t.mcc || '—', fmtMinor(t.amount_minor), t.currency || '—', formatLabel(t.type || '—'), formatLabel(t.status || '—'), formatLabel(t.decline_reason || '—'), fmtDate(t.created_at)]),
      { styles: { fontSize: 6, cellPadding: 1.2 } }
    );
    // Card txn type breakdown
    const typeMap: Record<string, number> = {};
    for (const t of cardTxns) { typeMap[t.type || 'other'] = (typeMap[t.type || 'other'] || 0) + 1; }
    const txColors = [[59,130,246], [16,185,129], [245,158,11], [249,115,22], [239,68,68]] as [number,number,number][];
    drawBarChart(
      Object.entries(typeMap).map(([t, c], i) => ({ label: formatLabel(t), value: c, color: txColors[i % txColors.length] })),
      'Card Transaction Types'
    );
    // Top merchants
    const mMap: Record<string, number> = {};
    for (const t of cardTxns) { if (t.merchant_name) mMap[t.merchant_name] = (mMap[t.merchant_name] || 0) + 1; }
    const topMerch = Object.entries(mMap).sort((a,b) => b[1] - a[1]).slice(0,5);
    if (topMerch.length > 0) {
      drawBarChart(topMerch.map(([m, c]) => ({ label: m.slice(0,20), value: c, color: PRIMARY as unknown as [number,number,number] })), 'Top Merchants');
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════════════
  const seen = new Set<string>();
  const dedupedTxn = txns.filter((t: any) => { if (seen.has(t.journal_entry_id)) return false; seen.add(t.journal_entry_id); return true; });
  sectionHeader(`Transactions (${dedupedTxn.length})`);
  if (dedupedTxn.length > 0) {
    renderTable(
      [['Ref', 'Description', 'Dir', 'Amount', 'CCY', 'Status', 'Date']],
      dedupedTxn.slice(0, 60).map((t: any) => [t.journal_entries?.tc_number || '—', (t.description || t.journal_entries?.description || '—').slice(0,45), t.dr_cr || '—', fmtMinor(t.amount_minor), t.currency || '—', formatLabel(t.journal_entries?.status || '—'), fmtDate(t.journal_entries?.created_at || t.created_at)]),
      { styles: { fontSize: 6.5, cellPadding: 1.8 } }
    );
    const credits = dedupedTxn.filter((t: any) => t.dr_cr === 'CREDIT').length;
    const debits = dedupedTxn.filter((t: any) => t.dr_cr === 'DEBIT').length;
    drawBarChart([{ label: 'Credits', value: credits, color: GREEN }, { label: 'Debits', value: debits, color: RED }], 'Transaction Volume');
  } else { emptyNote('No transactions recorded.'); }

  // ══════════════════════════════════════════════════════════════════════════
  // 8. AUDIT TRAIL
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader(`Admin Audit Trail (${audit.length})`);
  if (audit.length > 0) {
    renderTable(
      [['Action', 'Performed By', 'IP', 'Date', 'Changes']],
      audit.map((e: any) => {
        const changes = e.after_state && typeof e.after_state === 'object' ? Object.entries(e.after_state).map(([k, v]) => `${formatLabel(k)}: ${v}`).join('; ') : '—';
        return [formatLabel(e.action), e.actor_email || 'System', e.ip_address || '—', fmtDateTime(e.created_at), changes.slice(0,90)];
      }),
      { styles: { fontSize: 6.5, cellPadding: 1.8 }, columnStyles: { 4: { cellWidth: 50 } } }
    );
  } else { emptyNote('No audit events recorded.'); }

  // ══════════════════════════════════════════════════════════════════════════
  // 8B. CUSTOMER ACTIVITY (Sessions, Logins, SCA)
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('Customer Activity');

  // Session stats summary
  const successfulLogins = loginAttempts.filter((a: any) => a.success).length;
  const failedLogins = loginAttempts.filter((a: any) => !a.success).length;
  const scaTotal = scaEvents.length;
  const lastActive = custSessions[0]?.last_activity_at || loginAttempts[0]?.created_at;
  kvRow('Successful Logins', String(successfulLogins));
  kvRow('Failed Login Attempts', String(failedLogins));
  kvRow('SCA Operations', String(scaTotal));
  kvRow('Last Activity', fmtDateTime(lastActive));

  if (custSessions.length > 0) {
    subHeader(`Session History (${custSessions.length})`);
    renderTable(
      [['Login Time', 'IP', 'Location', 'Device', 'Last Activity', 'End Reason']],
      custSessions.slice(0, 20).map((s: any) => [
        fmtDateTime(s.login_at), s.ip_address || '—',
        s.city && s.country ? `${s.city}, ${s.country}` : s.country || '—',
        formatLabel(s.device_type || '—'), fmtDateTime(s.last_activity_at),
        s.logout_reason ? formatLabel(s.logout_reason) : s.expired_at ? 'Expired' : 'Active',
      ]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
  }

  if (loginAttempts.length > 0) {
    subHeader(`Login Attempts (${loginAttempts.length})`);
    renderTable(
      [['Time', 'IP', 'Location', 'Result', 'Failure Reason']],
      loginAttempts.slice(0, 30).map((a: any) => [
        fmtDateTime(a.created_at), a.ip_address || '—',
        a.city && a.country ? `${a.city}, ${a.country}` : a.country || '—',
        a.success ? '✓ Success' : '✗ Failed', a.success ? '—' : formatLabel(a.failure_reason || 'unknown'),
      ]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
    // Login success chart
    drawBarChart([
      { label: 'Successful', value: successfulLogins, color: GREEN as unknown as [number,number,number] },
      { label: 'Failed', value: failedLogins, color: RED as unknown as [number,number,number] },
    ], 'Login Attempt Outcomes');
  }

  if (scaEvents.length > 0) {
    subHeader(`SCA / Step-Up Events (${scaEvents.length})`);
    renderTable(
      [['Operation', 'Outcome', 'Factor', 'IP', 'Failure Reason', 'Time']],
      scaEvents.slice(0, 20).map((e: any) => [
        formatLabel(e.operation), formatLabel(e.outcome), e.factor_type || '—',
        e.ip_address?.split(',')[0]?.trim() || '—', e.failure_reason || '—', fmtDateTime(e.created_at),
      ]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
  }

  if (custSessions.length === 0 && loginAttempts.length === 0 && scaEvents.length === 0) {
    emptyNote('No customer activity recorded.');
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('Compliance');

  subHeader(`Screening Checks (${sChecks.length})`);
  if (sChecks.length > 0) {
    renderTable(
      [['Check Type', 'Provider', 'Status', 'Risk Level', 'Subject', 'Provider Ref', 'Date']],
      sChecks.map((c: any) => [formatLabel(c.check_type), formatLabel(c.provider || '—'), formatLabel(c.status), formatLabel(c.risk_level || '—'), formatLabel(c.subject_type), c.provider_reference_id?.slice(0,16) || '—', fmtDate(c.created_at)])
    );
  } else { emptyNote('No screening checks performed.'); }

  if (sHits.length > 0) {
    subHeader(`Screening Hits (${sHits.length})`);
    renderTable(
      [['Type', 'Score', 'List', 'Source', 'Status', 'Disposition', 'Disposition Notes', 'Date']],
      sHits.map((h: any) => [formatLabel(h.hit_type), h.match_score != null ? String(h.match_score) : '—', h.list_name || '—', h.list_source || '—', formatLabel(h.status), formatLabel(h.disposition || '—'), (h.disposition_notes || '—').slice(0,40), fmtDate(h.created_at)]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
  }

  if (alerts.length > 0) {
    subHeader(`Monitoring Alerts (${alerts.length})`);
    renderTable(
      [['Alert Type', 'Rule', 'Severity', 'Status', 'Description', 'Created']],
      alerts.map((a: any) => [formatLabel(a.alert_type), a.rule_id || '—', formatLabel(a.severity), formatLabel(a.status), (a.description || '—').slice(0,50), fmtDateTime(a.created_at)])
    );
    const sevMap: Record<string, number> = {};
    for (const a of alerts) { sevMap[a.severity] = (sevMap[a.severity] || 0) + 1; }
    const sevCol: Record<string, readonly [number,number,number]> = { critical: RED, high: AMBER, medium: SUNSET, low: GREEN };
    drawBarChart(Object.entries(sevMap).map(([s, c]) => ({ label: formatLabel(s), value: c, color: sevCol[s] || PRIMARY })), 'Alerts by Severity');
  }

  if (cases.length > 0) {
    subHeader(`Compliance Cases (${cases.length})`);
    renderTable(
      [['Case #', 'Type', 'Priority', 'Status', 'Decision', 'Assigned', 'Risk', 'Opened', 'Closed']],
      cases.map((c: any) => [c.case_number || '—', formatLabel(c.case_type), formatLabel(c.priority), formatLabel(c.status), formatLabel(c.decision || '—'), c.assigned_to || '—', c.risk_rating || '—', fmtDate(c.created_at), fmtDate(c.closed_at)]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
  }

  if (decls.length > 0) {
    subHeader(`AML Declarations (${decls.length})`);
    for (const d of decls as any[]) {
      checkBreak(30);
      const dFields = [
        ['Type', formatLabel(d.declaration_type)], ['Source of Funds', formatLabel(d.source_of_funds)],
        ['SoF Detail', d.source_of_funds_detail], ['Source of Wealth', d.source_of_wealth],
        ['SoW Detail', d.source_of_wealth_detail], ['Transaction Purpose', formatLabel(d.transaction_purpose)],
        ['Purpose Detail', d.transaction_purpose_detail], ['PEP', d.is_pep ? 'YES' : 'No'],
        ['Has PEP Family Member', d.has_pep_family_member ? 'YES' : 'No'],
        ['PEP Position', d.pep_position], ['PEP Country', d.pep_country],
        ['Acting Own Behalf', d.acting_on_own_behalf ? 'Yes' : 'No'],
        ['Beneficial Owner', d.beneficial_owner_name],
        ['Tax Compliant', d.tax_compliant_declaration ? 'Yes' : 'No'],
        ['Status', formatLabel(d.status)], ['Declared', fmtDateTime(d.declared_at)],
      ];
      for (const [k, v] of dFields) { if (v) kvRow(k, safe(v)); }
      y += 3;
    }
  }

  if (reviews.length > 0) {
    subHeader(`Periodic Reviews (${reviews.length})`);
    renderTable(
      [['Risk Tier', 'Freq', 'Status', 'Next Review', 'Last Review', 'Outcome', 'Notes']],
      reviews.map((r: any) => [formatLabel(r.risk_tier), `${r.frequency_months}m`, formatLabel(r.status), fmtDate(r.next_review_at), fmtDate(r.last_review_at), formatLabel(r.review_outcome || '—'), (r.review_notes || '—').slice(0,40)])
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // 10. CONTROLS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader('Account Controls');
  if (controls.length > 0) {
    renderTable(
      [['Account', 'CCY', 'Outbound', 'Inbound', 'Rail Scope', 'Product Scope', 'Reason', 'Set By', 'Set At']],
      controls.map((c: any) => [c.account_name || c.account_id?.slice(0,8) || '—', c.account_currency || '—', c.outbound_allowed ? '✓ Allowed' : '✗ Blocked', c.inbound_allowed ? '✓ Allowed' : '✗ Blocked', c.rail_scope?.join(', ') || 'All', c.product_scope?.join(', ') || 'All', c.reason || '—', c.set_by || '—', fmtDateTime(c.set_at)]),
      { styles: { fontSize: 6.5, cellPadding: 1.5 } }
    );
  } else { emptyNote('No account controls configured — all operations permitted.'); }

  subHeader('Client Status & Security');
  kvRow('Current Status', formatLabel(profile.status));
  kvRow('MFA Enabled', profile.mfa_enabled ? 'Yes' : 'No');
  kvRow('MFA Method', formatLabel(profile.mfa_method || '—'));
  kvRow('MFA Required', profile.mfa_required ? 'Yes' : 'No');
  kvRow('Back-Office Reviewed', profile.backoffice_reviewed ? 'Yes' : 'No');
  kvRow('Reviewed At', fmtDateTime(profile.backoffice_reviewed_at));
  kvRow('Reviewed By', profile.backoffice_reviewed_by || '—');
  kvRow('Is Employee Test', profile.is_employee_test ? 'Yes' : 'No');
  if (profile.employee_test_notes) kvRow('Test Notes', String(profile.employee_test_notes));

  // ══════════════════════════════════════════════════════════════════════════
  // 11. WHITELISTS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader(`Whitelists (${whitelist.length})`);
  if (whitelist.length > 0) {
    renderTable(
      [['Dir', 'Type', 'ID Type', 'Identifier', 'Name', 'Country', 'CCY', 'Max Amt', 'Rail', 'Status', 'Valid', 'Usage']],
      whitelist.map((w: any) => [formatLabel(w.direction_scope || '—'), formatLabel(w.entry_type || '—'), formatLabel(w.identifier_type || '—'), w.identifier_value || '—', w.display_name || '—', w.country || '—', w.currency_scope || 'Any', w.max_amount_minor != null ? fmtMinor(w.max_amount_minor) : '∞', w.rail_scope || 'All', formatLabel(w.status), `${fmtDate(w.valid_from)} → ${fmtDate(w.valid_to)}`, String(w.usage_count ?? 0)]),
      { styles: { fontSize: 6, cellPadding: 1.2 } }
    );
  } else { emptyNote('No whitelist entries configured.'); }

  // ══════════════════════════════════════════════════════════════════════════
  // 12. MESSAGES
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeader(`Messages (${conversations.length} conversations, ${messages.length} messages)`);
  if (conversations.length > 0) {
    subHeader('Conversations');
    renderTable(
      [['ID', 'Participant A', 'Participant B', 'Last Message', 'Created']],
      conversations.map((c: any) => [c.id?.slice(0,8), c.participant_a?.slice(0,8), c.participant_b?.slice(0,8), fmtDateTime(c.last_message_at), fmtDate(c.created_at)])
    );
    if (messages.length > 0) {
      subHeader('Recent Messages');
      renderTable(
        [['From', 'Content', 'Type', 'Read', 'Sent']],
        messages.slice(0,30).map((m: any) => [m.sender_id === clientId ? 'Client' : 'Admin', (m.content || '—').slice(0,60), formatLabel(m.message_type || 'text'), m.read_at ? 'Yes' : 'No', fmtDateTime(m.created_at)]),
        { styles: { fontSize: 6.5, cellPadding: 1.8 } }
      );
    }
  } else { emptyNote('No messages found.'); }

  // ══════════════════════════════════════════════════════════════════════════
  // END MATTER
  // ══════════════════════════════════════════════════════════════════════════
  checkBreak(24);
  y += 6;
  doc.setDrawColor(...NAVY); doc.setLineWidth(0.5); doc.line(mL, y, pageW - mR, y); y += 5;
  doc.setFillColor(...GRAY_50); doc.roundedRect(mL, y, cW, 16, 2, 2, 'F');
  doc.setFontSize(7); doc.setTextColor(...GRAY_500);
  doc.text('This report was generated by the Paynovus Admin Portal and contains confidential client information.', mL + 4, y + 5);
  doc.text('Unauthorized distribution is prohibited under Regulation (EU) 2016/679 (GDPR).', mL + 4, y + 9);
  doc.text(`© ${new Date().getFullYear()} Paynovus EMI Ltd. Licensed by the Bulgarian National Bank. All rights reserved.`, mL + 4, y + 13);

  // ── Branded letterheads + watermark on ALL pages ──────────────────────────
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    if (i > 1) {
      // Watermark
      try {
        doc.saveGraphicsState();
        doc.setGState(new (doc as any).GState({ opacity: 0.04 }));
        doc.addImage(paynovusWatermark, 'PNG', pageW / 2 - 40, pageH / 2 - 50, 80, 100);
        doc.restoreGraphicsState();
      } catch { /* skip */ }

      // Header letterhead (logo banner)
      try { doc.addImage(paynovusLetterheadFooter, 'JPEG', 0, 0, pageW, 25); } catch {
        doc.setFillColor(...NAVY); doc.rect(0, 0, pageW, 20, 'F');
        try { doc.addImage(paynovusLogoHeader, 'PNG', mL, 4, 45, 14); } catch {}
      }
      // Page number in header
      doc.setFontSize(7.5); doc.setTextColor(...GRAY_500);
      doc.text(`Page ${i} of ${totalPages}`, pageW - mR, 16, { align: 'right' });

      // Footer letterhead (address)
      try { doc.addImage(paynovusLetterheadHeader, 'JPEG', 0, pageH - 22, pageW, 22); } catch {
        doc.setDrawColor(...GRAY_300); doc.line(mL, pageH - 22, pageW - mR, pageH - 22);
        doc.setFontSize(7); doc.setTextColor(...GRAY_500);
        doc.text('Pozitano 2 sq., Perform Business Center, fl. 3, 1000 Sofia, Bulgaria', mL, pageH - 14);
        doc.text('office@paynovus.com | paynovus.com', pageW - mR, pageH - 14, { align: 'right' });
      }
    }

    // CONFIDENTIAL strip at bottom of every page
    doc.setFontSize(6); doc.setTextColor(...GRAY_500);
    doc.text(`CONFIDENTIAL — ${ts} — ${displayName} — ${profile.system_id || ''}`, pageW / 2, pageH - 4, { align: 'center' });
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const slug = displayName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  doc.save(`paynovus-client-report-${slug}-${format(new Date(), 'yyyyMMdd')}.pdf`);
}
